import { useCallback, useEffect, useState } from 'react';
import { withAuth } from '../hoc/with-auth';
import { BasePageProps } from '../../layouts/base-layout';
import { useClearAdvisories } from '../../hooks/use-advisories';
import { createGlobalStyle, styled } from 'styled-components';
import { accountsMedia } from '../../styles/bootstrap-breakpoints';
import { Modal } from 'gocclr-shared-ui/dist/elements';
import { validateAgencyCommissionAccess, validateAgencyTnCAcceptance } from '../../services/agency.service';
import AcknowledgementTermsAndConditionsRedirection from './acknowledgement-terms-and-conditions-redirection';
import { AcknowledgementTermsAndConditionsRender } from './acknowledgement-terms-and-conditions.render';
import { ApiError } from 'gocclr-shared-ui/dist/helpers';

const AcknowledgementCommissionsModalOverlay = createGlobalStyle`
    .cmodal__overlay {
        backdrop-filter: blur(20px);
    }
`;

const AcknowledgementCommissionsModal = styled(Modal)`
    .cmodal__container  {
        border-radius: 0.5rem;
    }

    ${accountsMedia.lessThan('Medium')`
    .cmodal__container {
        border-radius: 0;
    }
    `}
`;

export interface AcknowledgementTermsAndConditionsProps extends BasePageProps {
    onAgreementComplete?: () => void;
}

function AcknowledgementTermsAndConditions(props: AcknowledgementTermsAndConditionsProps) {
    const { advisoriesApi, onAgreementComplete } = props;
    const [showModal, setShowModal] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [shouldRedirectToCommissions, setShouldRedirectToCommissions] = useState<boolean>(false);

    useClearAdvisories(advisoriesApi);

    const getDetailAdvisories = useCallback((error: ApiError): void => {
        if (!error) {
            return;
        }

        if (error.message) {
            advisoriesApi.add({ description: error.message, type: 'error' });
        }

        if (error.details) {
            for (let i = 0; i < error.details.length; i++) {
                const detail = error.details[i];
                getDetailAdvisories(detail);
            }
        }
    }, [advisoriesApi]);

    useEffect(() => {
        Promise.all([
            validateAgencyTnCAcceptance(),
            validateAgencyCommissionAccess()
        ])
            .then(([needsToBeAcknowledgeAgencyTnC, hasAccessAgencyCommission]) => {
                if (!hasAccessAgencyCommission || !needsToBeAcknowledgeAgencyTnC) {
                    onAgreementComplete?.();
                }
                setShowModal(hasAccessAgencyCommission && needsToBeAcknowledgeAgencyTnC);
            }).catch((error) => {
                getDetailAdvisories(error);
            }).finally(() => {
                setIsLoading(false);
            });
    }, []);

    const onCloseAcknowledgementTermsAndConditions = () => {
        setShowModal(false);
        onAgreementComplete?.();
    }

    const onAgreeAcknowledgementTermsAndConditions = () => {
        setShowModal(false);
        setShouldRedirectToCommissions(true);
        onAgreementComplete?.();
    }

    const onDisagreeAcknowledgementTermsAndConditions = () => {
        onAgreementComplete?.();
    }

    if (shouldRedirectToCommissions) {
        return <AcknowledgementTermsAndConditionsRedirection />;
    }

    return (
        <>
            <AcknowledgementCommissionsModalOverlay />
            <AcknowledgementCommissionsModal
                className="acknowledgement-commissions__modal"
                isOpen={showModal}
                isShell
                onRequestClose={onCloseAcknowledgementTermsAndConditions}
            >
                <AcknowledgementTermsAndConditionsRender
                    isLoading={isLoading}
                    onAgree={onAgreeAcknowledgementTermsAndConditions}
                    onDisagree={onDisagreeAcknowledgementTermsAndConditions}
                />
            </AcknowledgementCommissionsModal>
        </>
    );
}

const AcknowledgementTermsAndConditionsWithAuth = withAuth(AcknowledgementTermsAndConditions);
export default AcknowledgementTermsAndConditionsWithAuth;